import { Component, computed, input, output, signal } from '@angular/core';
import { FilterChange, PendingPresetChange } from '../models/preset-change.models';
import { PresetPreviewService } from '../services/preset-preview.service';
import { POE1_PROGRESS_PRESETS, POE2_PROGRESS_PRESETS } from '../services/presets.service';
import { ArrayDiffItem, buildArrayDiff, formatChangeValue, isPrimitiveArray } from '../services/filter-diff.util';
import { BaseComponent } from '../common/base.component';
import { TieringMode } from '../models/enums';
import { TieringCutoffHint } from '../models/tiering-cutoff-hint.models';
import { CurrencyTierService } from '../services/tiering/currency-tier.service';
import { UniquesTierService } from '../services/tiering/uniques-tier.service';
import { buildTieringCutoffHint } from '../services/tiering/tiering-cutoff-hint.util';
import { TieringCutoffHintComponent } from '../common/tiering-cutoff-hint/tiering-cutoff-hint.component';

const SECTION_LABELS: Record<string, string> = {
  leagueStart: 'League Start',
  currency: 'Currency',
  uniques: 'Uniques',
  other: 'Other Items',
  myEquipment: 'Favourite Equipment',
  otherEquipment: 'Other Equipment',
  remainingEquipment: 'Remaining Equipment',
};

interface DisplayChange extends FilterChange {
  formattedCurrentValue: string;
  formattedNewValue: string;
  arrayDiffItems: ArrayDiffItem[] | null;
  tieringCutoffHint: TieringCutoffHint | null;
}

const TIERING_STRICTNESS: TieringMode[] = ['Early', 'Normal', 'Strict', 'VeryStrict'];

interface SectionGroup {
  label: string;
  changes: DisplayChange[];
}

interface ClassPresetEntry {
  key: string;
  img: string;
}

@Component({
  selector: 'app-preset-changes-dialog',
  imports: [TieringCutoffHintComponent],
  templateUrl: './preset-changes-dialog.component.html',
  styleUrls: ['../common/dialogs.scss', './preset-changes-dialog.component.scss'],
})
export class PresetChangesDialogComponent extends BaseComponent {
  pendingChange = input.required<PendingPresetChange>();
  confirm = output<void>();
  cancel = output<void>();

  showOnlyUserModified = signal(false);

  progressPresets = this.poe2 ? POE2_PROGRESS_PRESETS : POE1_PROGRESS_PRESETS;

  classPresets = computed<ClassPresetEntry[]>(() => {
    const classes = this.poe2
      ? ['Warrior', 'Mercenary', 'Ranger', 'Huntress', 'Monk', 'Witch', 'Sorceress', 'Druid']
      : ['Marauder', 'Duelist', 'Ranger', 'Scion', 'Shadow', 'Witch', 'Templar'];
    return classes.map(cls => ({
      key: cls,
      img: `${this.imgPrefix}/img/presets/${cls.toLowerCase()}.webp`,
    }));
  });

  totalChangeCount = computed(() => this.pendingChange().changes.length);

  userModifiedChangeCount = computed(() => this.pendingChange().changes.filter(c => c.isUserModified).length);

  changesBySection = computed<SectionGroup[]>(() => {
    const changes = this.showOnlyUserModified()
      ? this.pendingChange().changes.filter(c => c.isUserModified)
      : this.pendingChange().changes;
    const sections = new Map<string, DisplayChange[]>();
    for (const change of changes) {
      const groupLabel = change.groupLabel ?? SECTION_LABELS[change.section] ?? change.section;
      if (!sections.has(groupLabel)) sections.set(groupLabel, []);
      const arrayDiffItems = isPrimitiveArray(change.currentValue) && isPrimitiveArray(change.newValue)
        ? buildArrayDiff(change.currentValue, change.newValue)
        : null;
      sections.get(groupLabel)!.push({
        ...change,
        formattedCurrentValue: formatChangeValue(change.currentValue),
        formattedNewValue: formatChangeValue(change.newValue),
        arrayDiffItems,
        tieringCutoffHint: this.buildTieringCutoffHint(change),
      });
    }
    return Array.from(sections.entries()).map(([label, ch]) => ({ label, changes: ch }));
  });

  isRevertingCurrentPreset = computed(() => {
    const pending = this.pendingChange();
    return !!pending.originalPreset && pending.newPreset === pending.originalPreset;
  });

  constructor(
    private presetPreviewService: PresetPreviewService,
    private currencyTierService: CurrencyTierService,
    private uniquesTierService: UniquesTierService,
  ) {
    super();
  }

  private buildTieringCutoffHint(change: FilterChange): TieringCutoffHint | null {
    const targetFilter = this.pendingChange().targetFilter;
    const from = change.currentValue as TieringMode;
    const to = change.newValue as TieringMode;
    if (!this.isStricterTieringMode(from, to)) return null;

    if (change.key === 'tieringMode') {
      return buildTieringCutoffHint('currency', to, targetFilter, this.currencyTierService, this.uniquesTierService);
    }
    if (change.key === 'divCardTieringMode') {
      return buildTieringCutoffHint('divCards', to, targetFilter, this.currencyTierService, this.uniquesTierService);
    }
    if (change.key === 'uniquesTieringMode') {
      return buildTieringCutoffHint('uniques', to, targetFilter, this.currencyTierService, this.uniquesTierService);
    }
    return null;
  }

  private isStricterTieringMode(from: TieringMode, to: TieringMode): boolean {
    if (from === 'Custom' || to === 'Custom') return false;
    const fromIndex = TIERING_STRICTNESS.indexOf(from);
    const toIndex = TIERING_STRICTNESS.indexOf(to);
    if (fromIndex === -1 || toIndex === -1) return false;
    return toIndex > fromIndex;
  }

  setPreviewPreset(preset: string): void {
    this.presetPreviewService.updatePreviewPreset(preset);
  }

  onConfirm(): void {
    this.confirm.emit();
  }

  onCancel(): void {
    this.cancel.emit();
  }

  disableAutoPreview(event: Event): void {
    event.preventDefault();
    this.settings.setAutoPreviewPresetChanges(false);
  }
}
